import { SidebarSection } from './common';
import {
  useAction,
  useActionsContext,
  useInScopeModel,
  useWatchableValue,
} from '../../actions/framework';
import { GraphModel } from '../../graph/graph.model';
import { useTabManager } from '../../tab-manager/tab-manager';
import { Routes } from '../../routes';
import { SideBarSection } from './types';
import { NewAgentAction } from '../../agent/new-agent.action';
import { DeleteAgentAction } from '../../agent/delete-agent.action';
import { RenameAgentAction } from '../../agent/rename-agent.action';
import { DuplicateAction } from '../../graph/duplicate.action';
import { DG } from '@cotera/sdk/core';
import { AgentModel } from '../../agent/agent.model';
import { Assert, using } from '@cotera/utilities';
import { DisposableTargetedModelContext } from '../../actions/etc';
import { v4 } from 'uuid';
import { IconName } from '@cotera/client/app/components/ui/icon';

export const Agents: React.FC = () => {
  const tabManager = useTabManager();
  const graphModel = useInScopeModel(GraphModel);
  const graph = useWatchableValue(graphModel!.graph);
  const newAgentAction = useAction(NewAgentAction);
  const deleteAgentAction = useAction(DeleteAgentAction);
  const renameAgentAction = useAction(RenameAgentAction);
  const duplicateAction = useAction(DuplicateAction);
  const context = useActionsContext();

  // Get all agents from the graph
  const agents: Array<{ name: string; node: DG.Graph.AgentNode }> = [];

  Object.entries(graph.nodeMap).forEach(([symbol, node]) => {
    if (node.t === 'agent') {
      agents.push({ name: symbol, node });
    }
  });

  const agentsSection: SideBarSection = {
    title: 'Agents',
    items: agents.map(({ name }) => {
      const quickActions = [
        {
          icon: 'pencil' as IconName,
          label: 'Rename Agent',
          onClick: async () => {
            Assert.assert(graphModel !== null);

            // Create a temporary AgentModel for this agent
            const agentModel = new AgentModel(
              {
                name,
                graph: graphModel.graph,
                compiler: graphModel.compiler,
              },
              {
                id: v4(),
                targeted: true,
                isNew: false,
              }
            );

            await using(
              new DisposableTargetedModelContext(context, agentModel),
              async () => {
                await renameAgentAction.execute({});
              }
            );
          },
          variant: 'grouped' as const,
        },
        {
          icon: 'trash' as IconName,
          label: 'Delete Agent',
          onClick: async () => {
            Assert.assert(graphModel !== null);

            // Create a temporary AgentModel for this agent
            const agentModel = new AgentModel(
              {
                name,
                graph: graphModel.graph,
                compiler: graphModel.compiler,
              },
              {
                id: v4(),
                targeted: true,
                isNew: false,
              }
            );

            await using(
              new DisposableTargetedModelContext(context, agentModel),
              async () => {
                await deleteAgentAction.execute({});
              }
            );
          },
          variant: 'grouped' as const,
        },
        {
          icon: 'copy' as IconName,
          label: 'Duplicate Agent',
          onClick: async () => {
            Assert.assert(graphModel !== null);

            // Create a temporary AgentModel for this agent
            const agentModel = new AgentModel(
              {
                name,
                graph: graphModel.graph,
                compiler: graphModel.compiler,
              },
              {
                id: v4(),
                targeted: true,
                isNew: false,
              }
            );

            await using(
              new DisposableTargetedModelContext(context, agentModel),
              async () => {
                await duplicateAction.execute();
              }
            );
          },
          variant: 'grouped' as const,
        },
      ];

      return {
        t: 'item' as const,
        title: name,
        icon: 'sparkles' as const,
        onClick: () => {
          tabManager.push({
            route: Routes.agent.forId(name).toString(),
            title: name,
            displayTitle: name,
            icon: 'sparkles',
          });
        },
        quickActions,
      };
    }),
    quickActions: [
      {
        icon: 'plus',
        label: 'New Agent',
        onClick: async () => {
          await newAgentAction.execute();
        },
      },
    ],
  };

  return <SidebarSection section={agentsSection} />;
};
